/**
 * Il lembo di nuvola attorno a un candidato, letto dal deposito.
 *
 * Formato `GFSW` (`gridfly.geo.swatch`): intestazione con origine, asse della
 * campata e punto piu' vicino del conduttore, poi i punti in `float32`
 * relativi all'origine e le classi in `uint8`. Si decodifica a mano: e' un
 * formato nostro e minimo, fatto per non portare una libreria (GI-241).
 */
export interface Swatch {
  /** Quanti punti. */
  count: number
  /** Il versore in pianta della campata, o nullo. */
  axis: [number, number] | null
  /** Il punto del conduttore piu' vicino, relativo all'origine, o nullo. */
  conductor: [number, number, number] | null
  /** x, y, z per punto, relativi al punto peggiore dell'anomalia. */
  xyz: Float32Array
  classes: Uint8Array
}

const MAGIC = 0x47465357 // «GFSW»
const HEAD = 4 + 4 + 4 + 24 + 8 + 12

export function decode(buffer: ArrayBuffer): Swatch {
  const view = new DataView(buffer)
  if (buffer.byteLength < HEAD || view.getUint32(0, false) !== MAGIC) throw new Error("non e' un lembo di nuvola")
  const version = view.getUint32(4, true)
  if (version !== 1) throw new Error(`versione del lembo sconosciuta: ${version}`)
  const count = view.getUint32(8, true)
  const ax = view.getFloat32(36, true)
  const ay = view.getFloat32(40, true)
  const cx = view.getFloat32(44, true)
  const cy = view.getFloat32(48, true)
  const cz = view.getFloat32(52, true)
  const xyz = new Float32Array(buffer.slice(HEAD, HEAD + count * 12))
  const classes = new Uint8Array(buffer.slice(HEAD + count * 12, HEAD + count * 13))
  return {
    count,
    axis: Number.isNaN(ax) ? null : [ax, ay],
    conductor: Number.isNaN(cx) ? null : [cx, cy, cz],
    xyz,
    classes,
  }
}

export async function fetchSwatch(url: string): Promise<Swatch> {
  const response = await fetch(url)
  if (!response.ok) throw new Error(`l'archivio ha risposto ${response.status}`)
  return decode(await response.arrayBuffer())
}

/** Il gettone di colore di una classe della nuvola (design code § 3.4, `--li-*`):
 *  il filo e' il piu' chiaro, la vegetazione sale per luminosita'. */
export function toneOf(cls: number): string {
  if (cls === 1) return 'li-ground'
  if (cls === 2) return 'li-veg1'
  if (cls === 3) return 'li-veg2'
  if (cls === 4) return 'li-veg3'
  if (cls === 5 || cls === 31) return 'li-build'
  if (cls === 14) return 'li-cond'
  if (cls === 15) return 'li-guard'
  if (cls === 16 || cls === 17) return 'li-tower'
  return 'li-none'
}
